import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { authState } from '../../store/atoms';
import { oneDayAPI } from '../../services/index';
import { OneDayRequest } from '../../types/api';
import Modal from '../../components/Modal';
import Tabs from '../../components/Tabs';
import { formatPhoneNumber, formatDateLong, formatBirthDate } from '../../utils/formatters';
import { STATUS_LABELS } from '../../config/constants';
import { useModal } from '../../hooks/common';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { PageLoading, EmptyState } from '../../components';

const OneDayRequests: React.FC = () => {
  const auth = useAtomValue(authState);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  // Infinite scroll hook
  const {
    items: requests,
    isLoading,
    isLoadingMore,
    error,
    refresh,
    handleScroll
  } = useInfiniteScroll<OneDayRequest>({
    fetchFunction: oneDayAPI.getRequests,
    limit: 200,
    searchTerm,
    statusFilter: selectedStatus
  });

  const scheduleModal = useModal<OneDayRequest>();
  const detailModal = useModal<OneDayRequest>();
  const isMobile = useIsMobile();

  // 데스크탑: 하단 고정 상세 패널
  const [selectedDetail, setSelectedDetail] = useState<OneDayRequest | null>(null);

  const openDetail = (request: OneDayRequest) => {
    if (isMobile) {
      detailModal.open(request);
    } else {
      setSelectedDetail(request);
    }
  };

  // 기본으로 가장 최신 신청의 상세 표시
  useEffect(() => {
    if (isMobile || requests.length === 0) return;
    setSelectedDetail(prev =>
      prev && requests.some(r => r.id === prev.id)
        ? requests.find(r => r.id === prev.id) || requests[0]
        : requests[0]
    );
  }, [requests, isMobile]);

  const [selectedScheduleOption, setSelectedScheduleOption] = useState<'preferred' | 'custom'>('preferred');
  const [selectedDateTime, setSelectedDateTime] = useState('');
  const [customDateTime, setCustomDateTime] = useState('');

  const statusLabels = STATUS_LABELS.ONEDAY_CLASS;

  const handleStatusUpdate = async (requestId: string, newStatus: string, scheduledAt?: string) => {
    try {
      setIsUpdating(requestId);
      const updateData: {
        status: string;
        scheduledAt?: string;
      } = { status: newStatus };

      if (scheduledAt) {
        updateData.scheduledAt = scheduledAt;
      }

      const response = await oneDayAPI.updateStatus(requestId, updateData);

      if (response.success) {
        refresh(); // 목록 새로고침

        // 사이드바 카운트 새로고침
        window.dispatchEvent(new Event('refreshPendingCounts'));

        // 상태별 토스트 메시지
        if (newStatus === 'contacted') {
          toast.success('연락완료 처리되었습니다');
        } else if (newStatus === 'scheduled') {
          toast.success('일정이 확정되었습니다');
        } else if (newStatus === 'completed') {
          toast.success('수업이 완료되었습니다');
        } else if (newStatus === 'cancelled') {
          toast.error('수업이 취소되었습니다');
        } else {
          toast.success('상태가 업데이트되었습니다');
        }
      } else {
        toast.error('상태 업데이트에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      toast.error('상태 업데이트 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleScheduleClass = (requestId: string) => {
    const request = requests.find(req => req.requestId === requestId);
    if (request) {
      setSelectedScheduleOption('preferred');
      setSelectedDateTime('');
      setCustomDateTime('');
      scheduleModal.open(request);
    }
  };

  // 희망 시간에서 선택 가능한 옵션들 파싱
  const parsePreferredTimes = (preferredTime: string) => {
    const options: string[] = [];

    // 예: "8/29(금요일): 21:00" 또는 "8/26(화요일): 14:00, 19:00" 형태
    const dateTimeGroups = preferredTime.split(' | ');

    dateTimeGroups.forEach(group => {
      // 새로운 형식: "8/29(금요일): 21:00" 또는 "8/26(화요일): 14:00, 19:00"
      const match = group.match(/(\d+)\/(\d+)\([월화수목금토일]요일\):\s*(.+)/);
      if (match) {
        const [, month, day, times] = match;
        const timeList = times.split(',').map(t => t.trim());
        const currentYear = new Date().getFullYear();

        timeList.forEach(time => {
          const dateTimeStr = `${currentYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${time}`;
          options.push(dateTimeStr);
        });
      }
    });

    return options;
  };

  const handleConfirmSchedule = async () => {
    if (!scheduleModal.data || !auth.userInfo) return;

    let scheduledAt = '';

    if (selectedScheduleOption === 'preferred') {
      if (!selectedDateTime) {
        toast.error('희망 시간을 선택해주세요!');
        return;
      }
      scheduledAt = new Date(selectedDateTime).toISOString();
    } else {
      if (!customDateTime) {
        toast.error('날짜와 시간을 입력해주세요!');
        return;
      }
      try {
        scheduledAt = new Date(customDateTime).toISOString();
      } catch (error) {
        toast.error('올바른 날짜 형식을 입력해주세요!');
        return;
      }
    }

    try {
      // 1. 원데이클래스 상태를 'scheduled'로 업데이트
      await handleStatusUpdate(scheduleModal.data.requestId, 'scheduled', scheduledAt);

      // 서버에서 자동으로 UnifiedSchedule에 일정을 추가함 (oneDayClassController.js 참조)
      toast.success('일정이 확정되고 선생님 캘린더에 추가되었습니다', { duration: 4000 });

    } catch (error) {
      const errorMessage = (error && typeof error === 'object' && 'response' in error)
        ? ((error as any).response?.data?.message || '일정 확정 중 오류가 발생했습니다.')
        : (error instanceof Error ? error.message : '일정 확정 중 오류가 발생했습니다.');
      toast.error(`일정 확정 실패: ${errorMessage}`);
    }

    scheduleModal.close();
  };

  if (isLoading && requests.length === 0) {
    return <PageLoading message="원데이클래스 신청 목록을 불러오는 중..." fullScreen />;
  }

  return (
    <div className="teacher-page list-detail-page">
      <TeacherPageHeader title="원데이클래스 관리">
        <Tabs
          options={[
            { value: '', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'contacted', label: '연락완료' },
            { value: 'scheduled', label: '일정확정' },
            { value: 'completed', label: '수업완료' },
            { value: 'cancelled', label: '취소됨' }
          ]}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
        />
        <input
          className="header-search"
          type="text"
          placeholder="이름·연락처 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </TeacherPageHeader>
      {/* 필터 (모바일 전용) */}
      <div className="content-header desktop-hidden">
        <Tabs
          options={[
            { value: '', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'contacted', label: '연락완료' },
            { value: 'scheduled', label: '일정확정' },
            { value: 'completed', label: '수업완료' },
            { value: 'cancelled', label: '취소됨' }
          ]}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
        />
      </div>

      {/* 신청 목록 */}
      <div className="requests-container" onScroll={handleScroll}>
        {error && (
          <div className="error-message" style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
            {error}
          </div>
        )}
        {requests.length === 0 && !isLoading ? (
          <EmptyState
            title="신청된 원데이클래스가 없습니다"
            description="새로운 신청이 들어오면 여기에 표시됩니다."
          />
        ) : (
          <>
            {/* 데스크탑 테이블 뷰 */}
            <div className="table-view">
              <div className="table-wrapper">
                <table className="lesson-requests-table">
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>연락처</th>
                      <th>희망시간</th>
                      <th>신청일</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr
                        key={request.id}
                        className={`status-row status-${request.status}`}
                        onClick={() => openDetail(request)}
                      >
                        <td>
                          <div className="name-cell">
                            <span className="name">{request.name}</span>
                            <span className="sub">{formatBirthDate(request.birthDate)}</span>
                          </div>
                        </td>
                        <td>{formatPhoneNumber(request.phoneNumber)}</td>
                        <td className="time-cell">
                          {request.preferredTime.length > 30 ? `${request.preferredTime.substring(0, 30)}...` : request.preferredTime}
                        </td>
                        <td>{formatBirthDate(request.createdAt)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {request.status === 'pending' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleScheduleClass(request.requestId)}
                                disabled={isUpdating === request.requestId}
                              >
                                일정확정
                              </button>
                              <button
                                className="btn-action reject"
                                onClick={() => handleStatusUpdate(request.requestId, 'cancelled')}
                                disabled={isUpdating === request.requestId}
                              >
                                취소
                              </button>
                            </div>
                          )}
                          {request.status === 'contacted' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleScheduleClass(request.requestId)}
                                disabled={isUpdating === request.requestId}
                              >
                                일정확정
                              </button>
                              <button
                                className="btn-action reject"
                                onClick={() => handleStatusUpdate(request.requestId, 'cancelled')}
                                disabled={isUpdating === request.requestId}
                              >
                                취소
                              </button>
                            </div>
                          )}
                          {request.status === 'scheduled' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleStatusUpdate(request.requestId, 'completed')}
                                disabled={isUpdating === request.requestId}
                              >
                                완료
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모바일 리스트 뷰 */}
            <div className="card-view">
              <div className="requests-list">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className={`request-item status-${request.status}`}
                    onClick={() => openDetail(request)}
                  >
                    <div className="item-row">
                      <span className="item-name">{request.name}</span>
                      <span className="item-date">{formatBirthDate(request.createdAt)}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-phone">{formatPhoneNumber(request.phoneNumber)}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-time">
                        {request.preferredTime.length > 25 ? `${request.preferredTime.substring(0, 25)}...` : request.preferredTime}
                      </span>
                    </div>
                    {(request.status === 'pending' || request.status === 'contacted' || request.status === 'scheduled') && (
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        {(request.status === 'pending' || request.status === 'contacted') && (
                          <button
                            className="btn-approve"
                            onClick={() => handleScheduleClass(request.requestId)}
                            disabled={isUpdating === request.requestId}
                          >
                            일정확정
                          </button>
                        )}
                        {request.status === 'scheduled' && (
                          <button
                            className="btn-approve"
                            onClick={() => handleStatusUpdate(request.requestId, 'completed')}
                            disabled={isUpdating === request.requestId}
                          >
                            완료
                          </button>
                        )}
                        {(request.status === 'pending' || request.status === 'contacted') && (
                          <button
                            className="btn-reject"
                            onClick={() => handleStatusUpdate(request.requestId, 'cancelled')}
                            disabled={isUpdating === request.requestId}
                          >
                            취소
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div className="spinner">추가 데이터 로딩 중...</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 데스크탑 하단 고정 상세 */}
      {selectedDetail && (
        <div className="request-detail-inline table-view">
          <div className="detail-main">
            <div className="detail-title-row">
              <div className="detail-title">신청 상세 — {selectedDetail.name}</div>
              <span className={`status-badge ${selectedDetail.status}`}>
                {statusLabels[selectedDetail.status]}
              </span>
              {(selectedDetail.status === 'pending' || selectedDetail.status === 'contacted') && (
                <div className="title-actions">
                  <button
                    className="mini-btn primary"
                    onClick={() => handleScheduleClass(selectedDetail.requestId)}
                    disabled={isUpdating === selectedDetail.requestId}
                  >
                    일정확정
                  </button>
                  <button
                    className="mini-btn danger"
                    onClick={() => handleStatusUpdate(selectedDetail.requestId, 'cancelled')}
                    disabled={isUpdating === selectedDetail.requestId}
                  >
                    취소하기
                  </button>
                </div>
              )}
              <button className="detail-close" onClick={() => setSelectedDetail(null)} aria-label="닫기">✕</button>
            </div>
            <div className="detail-grid">
              <div>
                <div className="dt">연락처</div>
                <div className="dd">{formatPhoneNumber(selectedDetail.phoneNumber)}</div>
              </div>
              <div>
                <div className="dt">희망 시간</div>
                <div className="dd">{selectedDetail.preferredTime}</div>
              </div>
              <div>
                <div className="dt">{selectedDetail.status === 'completed' ? '수업일' : '예정일'}</div>
                <div className="dd">
                  {selectedDetail.scheduledAt && (selectedDetail.status === 'scheduled' || selectedDetail.status === 'completed')
                    ? formatDateLong(selectedDetail.scheduledAt, true)
                    : '—'}
                </div>
              </div>
              <div>
                <div className="dt">생년월일 · 신청일</div>
                <div className="dd">{formatBirthDate(selectedDetail.birthDate)} · {formatBirthDate(selectedDetail.createdAt)}</div>
              </div>
            </div>
            {selectedDetail.message && (
              <div className="detail-message">{selectedDetail.message}</div>
            )}
          </div>
        </div>
      )}

      {/* 일정 확정 모달 */}
      <Modal
        isOpen={scheduleModal.isOpen}
        onClose={scheduleModal.close}
        title={scheduleModal.data ? `일정 확정 - ${scheduleModal.data.name}` : '일정 확정'}
        size="md"
        width="800px"
      >
        <div className="schedule-options">
          <div className="option-group">
            <label className="radio-label">
              <input
                type="radio"
                name="scheduleOption"
                value="preferred"
                checked={selectedScheduleOption === 'preferred'}
                onChange={() => setSelectedScheduleOption('preferred')}
              />
              <span>사용자 희망 시간에서 선택</span>
            </label>

            {selectedScheduleOption === 'preferred' && scheduleModal.data && (
              <div className="preferred-times">
                {parsePreferredTimes(scheduleModal.data.preferredTime).map((dateTime, index) => (
                  <label key={index} className="time-option">
                    <input
                      type="radio"
                      name="preferredDateTime"
                      value={dateTime}
                      checked={selectedDateTime === dateTime}
                      onChange={(e) => setSelectedDateTime(e.target.value)}
                    />
                    <span>
                      {new Date(dateTime).toLocaleDateString('ko-KR', {
                        month: 'long',
                        day: 'numeric',
                        weekday: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </label>
                ))}
                {parsePreferredTimes(scheduleModal.data.preferredTime).length === 0 && (
                  <p className="no-preferred-times">파싱 가능한 희망 시간이 없습니다. 기타 옵션을 선택해주세요.</p>
                )}
              </div>
            )}
          </div>

          <div className="option-group">
            <label className="radio-label">
              <input
                type="radio"
                name="scheduleOption"
                value="custom"
                checked={selectedScheduleOption === 'custom'}
                onChange={() => setSelectedScheduleOption('custom')}
              />
              <span>기타 (직접 입력)</span>
            </label>

            {selectedScheduleOption === 'custom' && (
              <div className="custom-datetime">
                <input
                  type="datetime-local"
                  value={customDateTime}
                  onChange={(e) => setCustomDateTime(e.target.value)}
                  className="form-input"
                />
                <small className="form-help">날짜와 시간을 직접 선택해주세요.</small>
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button
            className="btn btn-primary"
            onClick={handleConfirmSchedule}
            disabled={
              (selectedScheduleOption === 'preferred' && !selectedDateTime) ||
              (selectedScheduleOption === 'custom' && !customDateTime)
            }
          >
            일정 확정
          </button>
          <button
            className="btn btn-secondary"
            onClick={scheduleModal.close}
          >
            취소
          </button>
        </div>
      </Modal>

      {/* 상세보기 모달 */}
      <Modal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        title="원데이클래스 신청 상세"
        size="sm"
      >
        {detailModal.data && (
          <div className="detail-content compact">
            <div className="detail-header">
              <div className="name-row">
                <span className="student-name">{detailModal.data.name}</span>
                <span className={`status-badge ${detailModal.data.status}`}>
                  {statusLabels[detailModal.data.status]}
                </span>
              </div>
              <div className="meta-info">
                {formatBirthDate(detailModal.data.birthDate)}
              </div>
            </div>

            <div className="detail-info-list">
              <div className="info-row">
                <span className="label">연락처</span>
                <span className="value">{formatPhoneNumber(detailModal.data.phoneNumber)}</span>
              </div>
              {detailModal.data.email && (
                <div className="info-row">
                  <span className="label">이메일</span>
                  <span className="value">{detailModal.data.email}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">희망시간</span>
                <span className="value">{detailModal.data.preferredTime}</span>
              </div>
              {detailModal.data.message && (
                <div className="info-row">
                  <span className="label">문의</span>
                  <span className="value">{detailModal.data.message}</span>
                </div>
              )}
              {detailModal.data.scheduledAt && (detailModal.data.status === 'scheduled' || detailModal.data.status === 'completed') && (
                <div className="info-row">
                  <span className="label">{detailModal.data.status === 'completed' ? '수업일' : '예정일'}</span>
                  <span className="value">{formatDateLong(detailModal.data.scheduledAt, true)}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">신청일</span>
                <span className="value">{formatBirthDate(detailModal.data.createdAt)}</span>
              </div>
            </div>

            {(detailModal.data.status === 'pending' || detailModal.data.status === 'contacted') && (
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const id = detailModal.data!.requestId;
                    detailModal.close();
                    handleScheduleClass(id);
                  }}
                  disabled={isUpdating === detailModal.data.requestId}
                >
                  일정확정
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    const id = detailModal.data!.requestId;
                    await handleStatusUpdate(id, 'cancelled');
                    detailModal.close();
                  }}
                  disabled={isUpdating === detailModal.data.requestId}
                >
                  {isUpdating === detailModal.data.requestId ? '처리중...' : '취소하기'}
                </button>
              </div>
            )}
            {detailModal.data.status === 'scheduled' && (
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const id = detailModal.data!.requestId;
                    await handleStatusUpdate(id, 'completed');
                    detailModal.close();
                  }}
                  disabled={isUpdating === detailModal.data.requestId}
                >
                  {isUpdating === detailModal.data.requestId ? '처리중...' : '수업완료'}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OneDayRequests; 